import { SelectInputOptionContent, SelectInput } from '@transferwise/components';
import { Flag } from '@wise/art';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { getCurrencyForCode, getAllCurrencies } from '../resources/currencies';

import defaultStyles from './CurrencySelector.module.less';

function sortByCurrencyCode(a, b) {
  return a.value.code.localeCompare(b.value.code);
}

function toCurrencyOption(currency) {
  return {
    type: 'option',
    value: currency,
  };
}

function generateCurrencyList(availableCurrencies) {
  return availableCurrencies
    ? availableCurrencies.map(getCurrencyForCode).filter((currency) => currency !== undefined)
    : getAllCurrencies();
}

function generateCurrencyOptions(searchTerm, translate, availableCurrencies) {
  return generateCurrencyList(availableCurrencies)
    .map((c) => ({
      code: c.code,
      name: translate({ id: c.nameTranslationKey }),
    }))
    .filter((c) => {
      const currency = `${c.code} ${c.name}`.toLowerCase();
      return currency.includes(searchTerm.toLowerCase());
    })
    .map(toCurrencyOption)
    .sort(sortByCurrencyCode);
}

const CurrencySelector = ({
  type,
  currencyCode,
  style,
  search,
  onSelectChange,
  availableCurrencies,
  disabled,
}) => {
  const { formatMessage } = useIntl();
  const derivedStyles = { ...style, ...defaultStyles };
  const items = useMemo(() => {
    return generateCurrencyOptions(search, formatMessage, availableCurrencies);
  }, [availableCurrencies, formatMessage, search]);

  const selectedValue = useMemo(() => {
    const selectedOption = items.find((item) => item.value.code === currencyCode);
    return selectedOption?.value;
  }, [currencyCode, items]);

  return (
    <SelectInput
      className={`${derivedStyles['currency-selector']}`}
      placeholder={formatMessage({ id: 'search-currency' })}
      disabled={disabled}
      filterable
      filterPlaceholder={formatMessage({ id: 'search-currency' })}
      items={items}
      value={selectedValue}
      onChange={(value) => onSelectChange(type)(value)}
      renderValue={(currency) => (
        <SelectInputOptionContent
          title={currency.code}
          note={currency.name}
          icon={<Flag code={currency.code} intrinsicSize={24} />}
        />
      )}
    />
  );
};
CurrencySelector.propTypes = {
  type: PropTypes.string.isRequired,
  currencyCode: PropTypes.string.isRequired,
  style: PropTypes.shape({}).isRequired,
  search: PropTypes.string,
  onSelectChange: PropTypes.func,
  availableCurrencies: PropTypes.arrayOf(PropTypes.string),
  disabled: PropTypes.bool,
};

CurrencySelector.defaultProps = {
  search: '',
  onSelectChange: () => {},
  availableCurrencies: null,
  disabled: false,
};

export default CurrencySelector;
